'use client';

import type { InfiniteData } from '@tanstack/react-query';
import { useMemo } from 'react';

// Define a generic type for page data
type PageData<TNode = unknown> = {
  nodes: TNode[];
  pageInfo: {
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    startCursor?: string | null;
    endCursor?: string | null;
    totalCount?: number;
  };
};

/**
 * A hook that combines data from an infinite query into a single data structure
 *
 * @template TNode The type of nodes in each page
 * @template TData The type of the data in each page
 * @template TReturn The type of the combined data structure to return
 * @param infiniteData The infinite query data to combine
 * @param combineDataFn Function that defines how to combine the data
 * @returns The combined data structure or undefined if no data is available
 */
function useCombineInfiniteQueryData<
  TNode = unknown,
  TData extends PageData<TNode> = PageData<TNode>,
  TReturn = TData,
>(
  infiniteData: InfiniteData<TData> | undefined,
  combineDataFn: (pages: TData[]) => TReturn | undefined = defaultCombineFunction as (pages: TData[]) => TReturn | undefined,
): TReturn | undefined {
  return useMemo(() => {
    if (infiniteData === undefined || infiniteData.pages.length === 0) {
      return undefined;
    }

    return combineDataFn(infiniteData.pages);
  }, [infiniteData, combineDataFn]);
}

/**
 * Default function to combine data from infinite query pages
 * Combines all nodes from all pages while keeping the latest pageInfo
 */
function defaultCombineFunction<
  TNode = unknown,
  T extends PageData<TNode> = PageData<TNode>,
>(pages: T[]): T | undefined {
  if (pages === undefined || pages.length === 0) {
    return undefined;
  }

  const lastPage = pages[pages.length - 1];
  if (lastPage === undefined) {
    return undefined;
  }

  // Get the last page's pageInfo for pagination metadata
  const lastPageInfo = lastPage.pageInfo;

  // Create a properly typed initial accumulator
  const initialAcc = {
    nodes: [] as TNode[],
    pageInfo: lastPageInfo,
  } as unknown as T;

  // Merge all nodes from all pages into a single array
  return pages.reduce((acc, page) => {
    return {
      ...acc,
      nodes: [...acc.nodes, ...page.nodes],
      // Keep using the latest pageInfo
      pageInfo: lastPageInfo,
    };
  }, initialAcc);
}

export default useCombineInfiniteQueryData;
